import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useWhatsAppSession, SessionStatus } from "@/hooks/useWhatsAppSession";
import { useAuth } from "@/context/AuthContext";

interface WhatsAppSessionPanelProps {
  sessionName: string;
}

function getStatusLabel(status: SessionStatus, t: (k: string) => string): string {
  const labels: Record<SessionStatus, string> = {
    idle: "wpp.readyToConnect",
    generating: "wpp.generatingToken",
    token_ready: "wpp.tokenGenerated",
    starting: "wpp.startingSession",
    qr_ready: "wpp.waitingForQR",
    connected: "wpp.sessionConnected",
    error: "wpp.connectionError",
  };
  return t(labels[status]);
}

function getSteps(t: (k: string) => string): { key: SessionStatus | "done"; label: string; icon: string }[] {
  return [
    { key: "token_ready", label: t("wpp.stepToken"), icon: "ri-key-2-line" },
    { key: "qr_ready", label: t("wpp.stepQR"), icon: "ri-qr-code-line" },
    { key: "connected", label: t("wpp.stepConnected"), icon: "ri-link" },
  ];
}

function getStepState(current: SessionStatus, stepKey: string): "done" | "active" | "pending" {
  if (current === "error" || current === "idle") return "pending";
  if (current === "connected") return "done";
  if (current === "generating" && stepKey === "token_ready") return "active";
  if ((current === "token_ready" || current === "starting") && stepKey === "token_ready") return "done";
  if (current === "qr_ready" && (stepKey === "token_ready" || stepKey === "qr_ready")) return stepKey === "qr_ready" ? "active" : "done";
  if (current === "starting" && stepKey === "qr_ready") return "active";
  return "pending";
}

export default function WhatsAppSessionPanel({ sessionName }: WhatsAppSessionPanelProps) {
  const { t } = useTranslation("dashboard");
  const { updateUser } = useAuth();
  const {
    wppToken,
    qrCode,
    status,
    error,
    version,
    isChecking,
    generateToken,
    startSession,
    checkConnection,
    checkStatus,
    disconnect,
  } = useWhatsAppSession(sessionName);

  const prevStatusRef = useRef<SessionStatus>(status);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    if (status === "connected" && prevStatus !== "connected" && prevStatus !== "idle") {
      const handleRegenerateToken = async () => {
        try {
          const AI_API_BASE = "https://wpp.debugin.com/ai";
          const res = await fetch(`${AI_API_BASE}/manage/${sessionName}/regenerate-token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: wppToken }),
          });
          if (res.ok) {
            const json = await res.json();
            if (json.success && json.data?.token) {
              updateUser({ token: json.data.token });
            }
          }
        } catch (err) {
          console.error("[WhatsAppSessionPanel] Failed to regenerate token:", err);
        }
      };
      handleRegenerateToken();
    }
    prevStatusRef.current = status;
  }, [status, sessionName, wppToken, updateUser]);

  useEffect(() => {
    if ((status === "starting" || status === "qr_ready") && !pollingRef.current) {
      pollingRef.current = setInterval(() => {
        checkStatus();
      }, 3000);
    }

    if (status === "connected" || status === "error" || status === "idle") {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [status, checkStatus]);

  const handleGenerateAndStart = async () => {
    const tokenOk = await generateToken();
    if (tokenOk) {
      await startSession();
    }
  };

  const isWorking = status === "generating" || status === "starting" || isChecking;

  const STEPS = getSteps(t);

  if (!sessionName) {
    return (
      <div className="bg-background-50 rounded-2xl border border-background-200/50 p-8 text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
          <i className="ri-information-line text-foreground-400 text-xl"></i>
        </div>
        <p className="text-foreground-500 text-sm">{t("wpp.noSession")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Card */}
      <div className="bg-background-50 rounded-2xl border border-background-200/50 overflow-hidden">

        {/* Header - WhatsApp-style green strip */}
        <div className="px-5 md:px-6 py-4 border-b border-background-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center">
                <i className="ri-whatsapp-fill text-white text-base"></i>
              </div>
              <div>
                <h3 className="font-heading text-base text-foreground-950">{t("wpp.title")}</h3>
              </div>
            </div>

            {/* Status Badge */}
            {status === "connected" && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                {t("wpp.statusConnected")}
              </span>
            )}
            {status === "qr_ready" && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                {t("wpp.statusWaiting")}
              </span>
            )}
            {isWorking && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium">
                <i className="ri-loader-4-line animate-spin text-xs"></i>
                {status === "generating" ? t("wpp.statusGenerating") : status === "starting" ? t("wpp.statusStarting") : t("wpp.statusChecking")}
              </span>
            )}
            {status === "idle" && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-foreground-300"></span>
                {t("wpp.readyToConnect")}
              </span>
            )}
            {status === "error" && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                {t("wpp.connectionError")}
              </span>
            )}
          </div>
        </div>

        {/* Steps Indicator - WhatsApp-style clean list */}
        <div className="px-5 md:px-6 py-3 border-b border-background-100">
          <div className="flex items-center">
            {STEPS.map((step, idx) => {
              const stepState = getStepState(status, step.key);
              const isLast = idx === STEPS.length - 1;

              return (
                <div key={step.key} className="flex items-center">
                  {/* Step circle + label */}
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 ${
                        stepState === "done"
                          ? "bg-emerald-500 text-white"
                          : stepState === "active"
                            ? "bg-emerald-100 text-emerald-600 ring-2 ring-emerald-200"
                            : "bg-background-100 text-foreground-400"
                      }`}
                    >
                      {stepState === "done" ? (
                        <i className="ri-check-line text-xs"></i>
                      ) : (
                        <i className={`${step.icon} text-xs`}></i>
                      )}
                    </div>
                    <span
                      className={`text-xs font-medium hidden sm:inline ${
                        stepState === "done"
                          ? "text-emerald-600"
                          : stepState === "active"
                            ? "text-foreground-800"
                            : "text-foreground-400"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>

                  {/* Connector line */}
                  {!isLast && (
                    <div className="flex-1 h-px mx-3 min-w-[24px]">
                      <div className={`h-full rounded-full transition-colors duration-500 ${
                        stepState === "done" ? "bg-emerald-300" : "bg-background-200"
                      }`}></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Body - Content based on status */}
        <div className="p-5 md:p-6">

          {/* Error State */}
          {error && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100 mb-4">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-red-700 text-sm font-medium">{t("wpp.errorTitle")}</p>
                <p className="text-red-600 text-xs mt-0.5">{error}</p>
              </div>
              <button
                onClick={() => {
                  if (status === "error") disconnect();
                }}
                className="text-red-500 hover:bg-red-100 rounded-lg p-1.5 cursor-pointer flex-shrink-0 transition-colors"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          )}

          {/* Idle State - Connect CTA */}
          {status === "idle" && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-5">
                <i className="ri-whatsapp-fill text-emerald-500 text-3xl"></i>
              </div>
              <h4 className="text-foreground-800 text-sm font-medium mb-1">{t("wpp.title")}</h4>
              <p className="text-foreground-500 text-xs max-w-xs mx-auto mb-6 leading-relaxed">
                {t("wpp.connectPrompt")}
              </p>
              <button
                onClick={handleGenerateAndStart}
                className="inline-flex items-center gap-2 bg-emerald-500 text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-emerald-600 active:scale-[0.98] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-flashlight-line text-sm"></i>
                {t("wpp.connectBtn")}
              </button>
            </div>
          )}

          {/* Generating / Starting Loading State */}
          {(status === "generating" || status === "starting") && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-4">
                <div className="w-8 h-8 rounded-full border-2 border-emerald-200 border-t-emerald-500 animate-spin"></div>
              </div>
              <p className="text-foreground-700 text-sm font-medium">
                {status === "generating" ? t("wpp.generatingDesc") : t("wpp.startingDesc")}
              </p>
              <p className="text-foreground-400 text-xs mt-1">
                {status === "generating" ? t("wpp.generatingSub") : t("wpp.startingSub")}
              </p>
            </div>
          )}

          {/* Token Ready State */}
          {status === "token_ready" && (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 mb-4">
                <i className="ri-shield-check-line text-emerald-500 text-2xl"></i>
              </div>
              <p className="text-foreground-800 text-sm font-medium mb-1">{t("wpp.tokenReady")}</p>
              <p className="text-foreground-400 text-xs mb-5">{t("wpp.scanTitle")}</p>
              <button
                onClick={startSession}
                className="inline-flex items-center gap-2 bg-emerald-500 text-white text-sm font-medium px-5 py-2.5 rounded-full hover:bg-emerald-600 active:scale-[0.98] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-qr-code-line text-sm"></i>
                {t("wpp.startSession")}
              </button>
            </div>
          )}

          {/* QR Code Ready State */}
          {status === "qr_ready" && qrCode && (
            <div className="text-center">
              <div className="inline-block bg-white rounded-2xl p-4 border border-background-200/70 mb-5">
                <img
                  src={qrCode}
                  alt="WhatsApp QR Code - Scan to connect"
                  className="w-52 h-52 object-contain"
                />
              </div>
              <h4 className="text-foreground-800 text-sm font-semibold mb-1">
                {t("wpp.scanTitle")}
              </h4>
              <p className="text-foreground-500 text-xs max-w-xs mx-auto mb-5 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("wpp.scanDesc") }}>
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={checkStatus}
                  disabled={isChecking}
                  className="inline-flex items-center gap-2 bg-emerald-500 text-white text-xs font-medium px-4 py-2.5 rounded-full hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 active:scale-[0.98]"
                >
                  {isChecking ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xs"></i>
                      {t("wpp.statusChecking")}...
                    </>
                  ) : (
                    <>
                      <i className="ri-refresh-line text-xs"></i>
                      {t("wpp.checkNow")}
                    </>
                  )}
                </button>
                <button
                  onClick={disconnect}
                  className="inline-flex items-center gap-2 text-foreground-500 text-xs font-medium px-4 py-2.5 rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap active:scale-[0.98]"
                >
                  <i className="ri-arrow-go-back-line text-xs"></i>
                  {t("wpp.startOver")}
                </button>
              </div>
              <p className="text-foreground-400 text-[10px] mt-3">
                {t("wpp.autoCheck")}
              </p>
            </div>
          )}

          {/* Connected State */}
          {status === "connected" && (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-50 mb-4">
                <i className="ri-check-double-line text-emerald-500 text-3xl"></i>
              </div>
              <h4 className="text-foreground-950 text-sm font-semibold mb-1">
                {t("wpp.connectedTitle")}
              </h4>
              <p className="text-foreground-500 text-xs mb-5">
                {t("wpp.connectedDesc").replace("{name}", sessionName)}
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={checkConnection}
                  disabled={isChecking}
                  className="inline-flex items-center gap-2 bg-emerald-500 text-white text-xs font-medium px-4 py-2.5 rounded-full hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 active:scale-[0.98]"
                >
                  {isChecking ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xs"></i>
                      Checking...
                    </>
                  ) : (
                    <>
                      <i className="ri-signal-wifi-line text-xs"></i>
                      {t("wpp.verifyConnection")}
                    </>
                  )}
                </button>
                <button
                  onClick={disconnect}
                  className="inline-flex items-center gap-2 text-foreground-500 text-xs font-medium px-4 py-2.5 rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap active:scale-[0.98]"
                >
                  <i className="ri-refresh-line text-xs"></i>
                  {t("wpp.startOver")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}